/**
 * Reconciliation result for scaffold collections such as rows and columns.
 */
export interface IScaffoldReconciliation<TItem extends { id: string }> {
  /** Indicates whether item identifiers changed between previous and current inputs. */
  hasChanged: boolean;
  /** Item identifiers newly present in the current input. */
  enteringIds: Set<string>;
  /** Updated list of items currently animating out. */
  nextLeavingItems: TItem[];
}

/**
 * Reconciles scaffold item collections to derive entering ids and leaving items.
 *
 * @param currentItems - Items from the current render.
 * @param previousItems - Items from the previous render snapshot.
 * @param leavingItems - Items already marked as leaving from previous renders.
 * @returns Reconciliation data used to update entering and leaving scaffold state.
 */
export const reconcileScaffoldItems = <TItem extends { id: string }>(
  currentItems: TItem[],
  previousItems: TItem[],
  leavingItems: TItem[],
): IScaffoldReconciliation<TItem> => {
  const currentItemIds = currentItems.map((item) => item.id);
  const previousItemIds = previousItems.map((item) => item.id);
  const currentItemIdSet = new Set(currentItemIds);
  const previousItemIdSet = new Set(previousItemIds);

  const hasChanged =
    currentItemIds.some((id) => !previousItemIdSet.has(id)) ||
    previousItemIds.some((id) => !currentItemIdSet.has(id));

  if (!hasChanged) {
    return {
      hasChanged: false,
      enteringIds: new Set<string>(),
      nextLeavingItems: leavingItems,
    };
  }

  const enteringIds = new Set(currentItemIds.filter((id) => !previousItemIdSet.has(id)));
  const reconciledLeavingItems = leavingItems.filter((item) => !currentItemIdSet.has(item.id));
  const alreadyLeavingIds = new Set(reconciledLeavingItems.map((item) => item.id));
  const newLeavingItems = previousItems.filter(
    (item) => !currentItemIdSet.has(item.id) && !alreadyLeavingIds.has(item.id),
  );

  return {
    hasChanged: true,
    enteringIds,
    nextLeavingItems: [...reconciledLeavingItems, ...newLeavingItems],
  };
};
